import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { type ArchPhase, nextPhase } from './arch-fsm';
import { parseArchHash } from '../utils/archUrl';

// ════════════════════════════════════════════════════════════════════════════
// Architecture-map FSM store — Phase 1 of plans/architecture-map-c4.md.
//
// Applies the workspace Frontend FSM pattern (server-config/docs/frontend-fsm-pattern.md):
//   1. Explicit FSM        — phases live in arch-fsm.ts (exported TRANSITIONS, pure nextPhase).
//   2. Single global view  — the arch map is ONE navigation surface (not session-keyed), so a
//                            flat store, not a Map. Still server-authoritative + persisted.
//   3. Server-authoritative — graphLoaded(graph) is the only truth signal; breadcrumb + current
//                            come FROM the server graph, never synthesized client-side.
//   4. Atomic transitions  — one intent = one named action (requestGraph / drillTo / goToCrumb /
//                            graphLoaded / graphFailed / selectNode / closeNode). The ArchPage is
//                            the wire adapter (it calls authFetch); this store only owns state.
//   5. Setpoint evals      — setpoint-evals/arch-map/ (SE-05 drives the FSM table).
//
// Persists the NAVIGATION POSITION only (current level+id) so a reload returns you to the same
// view; the graph + phase are reset and re-fetched (pattern rule 3 — never trust a persisted live
// graph). The next GET /arch/graph is authoritative.
// ════════════════════════════════════════════════════════════════════════════

// ─── Wire types (mirror backend arch-graph.util ArchGraph) ──────────────────────
export type ArchKind = 'graph' | 'list';
export interface ArchNodeWire {
  id: string;
  label: string;
  docPath: string | null;
  hasChildren: boolean;
  childTarget?: string;
  /** Full-page route to navigate to on tap, INSTEAD of opening docPath (cross-link to a shipped route).
   *  Preferred over docPath when present; docPath is the degrade fallback. */
  routePath?: string;
  /** Setpoint Eval count for this node (project), when > 0. */
  seCount?: number;
  /** SE drill target (`#arch/SE/<project>`) — an orthogonal drill into this node's setpoints. */
  seTarget?: string;
  /** `kind:'list'` compliance gap: canonical SEs missing a GIVEN/WHEN/THEN README (omitted when 0). */
  seGap?: number;
  /** Leaf SE (`kind:'list'` suite view): this SE has no README mermaid — per-row gap signal. */
  missingMermaid?: boolean;
  // ─── Roadmap (RM) dimension — mirror of backend ArchNode (arch-graph.util.ts) ───
  /** Roadmap drill target (`#arch/RM/<project>`) — L0 project nodes only (the 📍 Program bridge). */
  roadmapTarget?: string;
  /** Coarse roadmap tense — the glance pill on L0/Atlas project rows; the per-plan glyph on RM rows. */
  roadmapTense?: 'now' | 'next' | 'done' | 'unknown';
  /** RM plan ROW only: verbatim Status-line text — rendered as an ESCAPED text child (S-RENDER). */
  roadmapRibbon?: string;
  /** RM plan ROW only: GFM checklist ratio for the progress ring (omitted when no checkboxes). */
  roadmapRing?: { done: number; total: number };
  /** RM plan ROW only: count of in-plan PR/issue refs — plural, non-authoritative "mentions". */
  roadmapMentions?: number;
  /** RM plan ROW only: best-effort "what's next" snippet. */
  roadmapNext?: string;
  // ─── AKB-Harness facet (HN) — read-only view of the harness control plane (Phase 2) ───
  /** HN binding ROW: resolved model id (seed defaultValue). Absent → a cross-link row, not a binding. */
  harnessModel?: string;
  /** HN binding ROW: true iff a `resolveModel('<key>')` call site exists (else seed-only/latent). */
  harnessLive?: boolean;
  /** HN binding ROW: seed danger tier — DATA (config-only) | CODE (hooks/skills, operator-gated). */
  harnessTier?: 'DATA' | 'CODE';
  // ─── RB (Runbooks) dimension — mirror of backend ArchNode (arch-graph.util.ts) ───
  /** Runbook count. L0 project node: total for that project's RB page. RB index bucket node: the
   *  bucket's own count (also embedded in the label). */
  rbCount?: number;
  /** RB drill target (`#arch/RB/<project>`) — L0 project nodes only, an orthogonal drill mirroring
   *  `seTarget`/`roadmapTarget`. */
  rbTarget?: string;
  /** RB leaf-tier row: audience badge ('operator' | 'agent' | 'both'). */
  rbAudience?: 'operator' | 'agent' | 'both';
  /** RB leaf-tier row: danger badge ('low' | 'medium' | 'high'). */
  rbDanger?: 'low' | 'medium' | 'high';
  /** RB leaf-tier row (`steps:` form): the procedure's steps, rendered inline. */
  rbSteps?: string[];
  /** RB leaf-tier row: true iff the entry's reference: path resolved under neither root — no docPath,
   *  but the row still renders (no dead click). */
  rbMissing?: boolean;
  // ─── AD (Architecture Docs) dimension — mirror of backend ArchNode (arch-graph.util.ts) ───
  /** Architecture-doc count. L0 project node: total under that project's `docs/architecture/`. AD
   *  index node: the project's own count (also embedded in the label). */
  adCount?: number;
  /** AD drill target (`#arch/AD/<project>`) — L0 project nodes only, mirrors `rbTarget`/`seTarget`. */
  adTarget?: string;
  /** AD leaf-tier row: the R7 provenance line ("Derived from: …"), verbatim, when the doc has one. */
  adProvenance?: string;
  // ─── HG (Human Guides) dimension — mirror of backend ArchNode (arch-graph.util.ts) ───
  /** Guide count. L0 project node: total under that project's `docs/guides/`. HG index node: the
   *  project's own count. */
  hgCount?: number;
  /** HG drill target (`#arch/HG/<project>`) — L0 project nodes only, mirrors `rbTarget`/`adTarget`. */
  hgTarget?: string;
  /** HG leaf-tier row: audience badge ('operator' | 'agent' | 'both'), from the guide's frontmatter. */
  hgAudience?: 'operator' | 'agent' | 'both';
}
export interface ArchCrumb {
  level: string;
  id: string;
  label: string;
}
export interface ArchGraphWire {
  level: string;
  id: string;
  /**
   * Renderer discriminator from the backend. `graph` → draw the mermaid diagram (real edges);
   * `list` → render `nodes[]` as a native vertical list (flat siblings — the Setpoint-Atlas nav
   * tiers). Tolerate absence (older backend / cache) by falling back to `graph`.
   */
  kind?: ArchKind;
  mermaid: string;
  nodes: ArchNodeWire[];
  breadcrumb: ArchCrumb[];
}

/** One row of the cross-corpus SE index (mirrors backend `SEIndexRow`, arch-graph.service.ts). */
export interface SEIndexRow {
  name: string;
  project: string;
  suite: string;
  hasMermaid: boolean;
  path: string | null;
  seTarget: string;
}

export interface ArchStoreShape {
  phase: ArchPhase;
  current: { level: string; id: string };
  /** Server-authoritative current graph. NOT persisted — re-fetched on load. */
  graph: ArchGraphWire | null;
  /** Selected node id (opens the node panel). null = no panel. */
  selectedNodeId: string | null;
  /** Tier-2 list filter query (client-side, over the current graph's nodes). Reset on navigation. */
  searchQuery: string;
  /**
   * Cross-corpus search palette query (Phase 2a) — null = palette closed, '' = open-empty (typing),
   * non-empty = a committed query. Filters `seIndex` (all projects/suites), NOT just the current
   * tier's nodes — the "front door across 485 SEs" the per-tier `searchQuery` above can't reach.
   * Reset on navigation (same rationale as `searchQuery`).
   */
  crossQuery: string | null;
  /** The flat cross-corpus SE index (`GET /arch/setpoints` `.se[]`), fetched once and cached here.
   *  null = not yet fetched. Filtered in memory by `crossQuery` — no per-keystroke round-trips. */
  seIndex: SEIndexRow[] | null;
  error: string | null;
  /**
   * Monotonic fetch trigger. Bumped on EVERY requestGraph so the ArchPage effect re-fires even when
   * the navigation position is unchanged — e.g. Retry from an error, or re-clicking ⟐ Setpoints from
   * the index. Without it a same-position re-navigation sets phase→loading but never re-fetches (hangs).
   */
  fetchNonce: number;
}

/** Exported for the simulator harness (setpoint-evals/arch-map/simulator) — the canonical "from
 *  scratch" shape each scenario resets to via `useArchStore.setState(initialState(), true)`. */
export function initialState(): ArchStoreShape {
  return {
    phase: 'idle',
    current: { level: 'L0', id: '' },
    graph: null,
    selectedNodeId: null,
    searchQuery: '',
    crossQuery: null,
    seIndex: null,
    error: null,
    fetchNonce: 0,
  };
}

export const useArchStore = create<ArchStoreShape>()(
  persist(initialState, {
    name: 'va-arch-map-v1',
    storage: createJSONStorage(() => localStorage),
    // Persist the navigation position only; reset live state on rehydrate so the
    // next fetch is authoritative (pattern rule 3 — never synthesize server state).
    partialize: (s) => ({ current: s.current }) as ArchStoreShape,
    onRehydrateStorage: () => (state) => {
      if (!state) return;
      useArchStore.setState({
        phase: 'idle',
        graph: null,
        selectedNodeId: null,
        searchQuery: '',
        crossQuery: null,
        seIndex: null,
        error: null,
      });
    },
  }),
);

// Cold-load precedence (mirrors useChatShell's `_initialSessionId`): a `#arch/<level>/<id>` hash
// (bookmark / shared link / reload) wins over the PERSISTED (localStorage) navigation position.
// Applied once, synchronously, right after the store's own synchronous persist-hydration
// (`createJSONStorage(() => localStorage)` hydrates within `create()` itself, above) — so ArchPage's
// very first render already sees the hash-derived position; no wasted throwaway fetch. This is
// establishing the STARTING configuration (phase is still 'idle', nothing has transitioned yet), not
// a state transition — same reasoning `initialState()` itself is exempt from going through a named
// action. The popstate case (hash changes while `/arch` stays mounted) goes through the named
// `restoreFromHash` action below instead.
if (typeof window !== 'undefined') {
  const seeded = parseArchHash(window.location.hash);
  if (seeded) useArchStore.setState({ current: seeded });
}

// ─── Named actions (one per intent — R-A2) ──────────────────────────────────────

/** idle | rendered | error → loading. The single navigation intent (initial / drill / crumb). */
export function requestGraph(level: string, id: string): void {
  const s = useArchStore.getState();
  const to = nextPhase(s.phase, 'requestGraph') ?? 'loading';
  useArchStore.setState({
    phase: to,
    current: { level, id },
    selectedNodeId: null,
    searchQuery: '', // a new position starts unfiltered
    crossQuery: null, // navigating away closes the cross-corpus search palette too
    error: null,
    fetchNonce: s.fetchNonce + 1,
  });
}

/** loading → rendered. ATOMIC: graph + current + breadcrumb all come from the server graph. */
export function graphLoaded(graph: ArchGraphWire): void {
  useArchStore.setState({
    phase: 'rendered',
    graph,
    current: { level: graph.level, id: graph.id },
    error: null,
  });
}

/**
 * Tier-2 list filter (R-A2 named action) — a data-only self-loop in `rendered`. Filters the current
 * graph's nodes client-side; never re-fetches. (`nextPhase` guard documents the legal self-loop.)
 */
export function setSearchQuery(q: string): void {
  nextPhase(useArchStore.getState().phase, 'setSearchQuery'); // documents the legal transition (R-A1)
  useArchStore.setState({ searchQuery: q });
}

/**
 * Cross-corpus search palette (R-A2 named action, Phase 2a) — ONE action covers open/type/close:
 * `runSearch(null)` closes the palette, `runSearch('')` opens it empty (focus, no query yet),
 * `runSearch(q)` commits typed text. Filters `seIndex` client-side (see ArchPage) — never a
 * per-keystroke round-trip. Mirrors `requestGraph` covering initial/drill/crumb as one intent.
 */
export function runSearch(query: string | null): void {
  nextPhase(useArchStore.getState().phase, 'runSearch'); // documents the legal transition (R-A1)
  useArchStore.setState({ crossQuery: query });
}

/** Cache the flat cross-corpus SE index, fetched once by ArchPage (supplementary data — not part of
 *  the phase machine, so this is a plain setter, not a self-loop action; mirrors how `graph` itself
 *  is written by the FSM-gated `graphLoaded` but ancillary UI data like this isn't). */
export function setSeIndex(rows: SEIndexRow[]): void {
  useArchStore.setState({ seIndex: rows });
}

/**
 * Deep-link restore (R-A2 named action, Phase 2b) — called from ArchPage on every `popstate` while
 * `/arch` is mounted (parsing the hash the browser just navigated to; cold-load precedence is
 * handled separately, above, by seeding `current` before this module finishes evaluating). `target`
 * is the result of `parseArchHash` — null when the hash doesn't belong to the `#arch/...` scheme at
 * all (e.g. `#file:...`, which CodeViewer owns; ArchPage must not call this for those).
 *
 * Two branches, matching the TWO ways this can legally fire:
 *   - `target` names a DIFFERENT position than `current` → delegate to `requestGraph` (the existing
 *     `*→loading` rows already cover this; no double-counted row here).
 *   - `target` is null OR already matches `current` (malformed/missing hash, or popping back from
 *     CodeViewer's `#file:` overlay to the still-current `#arch:` entry) → nothing to refetch, but
 *     it's a real, nameable "settled" event (R-A2), not a silent no-op — the `rendered→rendered`
 *     self-loop this function is named for.
 */
export function restoreFromHash(target: { level: string; id: string } | null): void {
  const s = useArchStore.getState();
  if (target && (target.level !== s.current.level || target.id !== s.current.id)) {
    requestGraph(target.level, target.id);
    return;
  }
  nextPhase(s.phase, 'restoreFromHash'); // documents the legal self-loop (R-A1)
}

export interface CrossSearchResult {
  suites: Array<{ project: string; suite: string; seTarget: string; count: number }>;
  ses: SEIndexRow[];
}

/**
 * Pure cross-corpus search filter (Phase 2a) — matches SE names AND suite names, case-insensitive,
 * against the flat `seIndex`. Suite results are deduped to one row per matching project/suite (with
 * its matched-SE count) even though the flat index carries one row per SE. Kept pure + exported so
 * it's unit-testable without mounting ArchPage (setpoint-evals/arch-map's cross-search SE).
 */
export function filterSeIndex(rows: SEIndexRow[], query: string): CrossSearchResult {
  const q = query.trim().toLowerCase();
  if (!q) return { suites: [], ses: [] };
  const ses = rows.filter((r) => r.name.toLowerCase().includes(q));
  const suiteMap = new Map<string, { project: string; suite: string; seTarget: string; count: number }>();
  for (const r of rows) {
    if (!r.suite.toLowerCase().includes(q)) continue;
    const key = `${r.project}/${r.suite}`;
    const existing = suiteMap.get(key);
    if (existing) existing.count++;
    else suiteMap.set(key, { project: r.project, suite: r.suite, seTarget: r.seTarget, count: 1 });
  }
  return { suites: Array.from(suiteMap.values()), ses };
}

/** loading → error. */
export function graphFailed(message: string): void {
  useArchStore.setState({ phase: 'error', error: message });
}

/** Drill intent (R-A2 named action) — descend one C4 level into a node's children. */
export function drillTo(level: string, id: string): void {
  requestGraph(level, id);
}

/**
 * Open the Setpoint Atlas (R-A2 named action) — the SE (behavioral-contract) dimension of the map.
 * No arg = the workspace SE index; a projectKey = that project's suites. Same fetch lifecycle as drill.
 */
export function drillToSetpoints(projectKey = ''): void {
  requestGraph('SE', projectKey);
}

/**
 * Open the AKB-Harness facet (R-A2 named action) — the READ-ONLY view of the harness control plane
 * (role→model bindings, source-derived from HARNESS_CONFIG_SEED). GLOBAL (no per-project id) — the
 * "two views of one control plane" cross-link to harness-studio's /harness editor. Same fetch
 * lifecycle as drill/setpoints. (No `parseArchTarget` token needed: this is a direct requestGraph,
 * not a node-attached target string. The `#arch/HN` deep-link IS shareable — `buildArchHash`/
 * `parseArchHash` are generic across all levels, HN included.)
 */
export function drillToHarness(): void {
  requestGraph('HN', '');
}

/**
 * Open the Runbooks (RB) dimension (R-A2 named action) — the procedural-knowledge dimension of the
 * map (WDS-1 registry/runbooks.yaml). No arg = the workspace RB index (one node per project bucket +
 * the 'workspace' catch-all); a projectKey = that project's runbooks. Same fetch lifecycle as
 * drill/setpoints/harness — a plain `requestGraph` wrapper, so NO new FSM transition row (R-A1).
 */
export function drillToRunbooks(projectKey = ''): void {
  requestGraph('RB', projectKey);
}

/**
 * Open the Architecture Docs (AD) dimension (R-A2 named action) — authored C4/architecture docs
 * (`docs/architecture/*.md`). No arg = the workspace AD index; a projectKey = that project's docs.
 * Same fetch lifecycle as drill/setpoints/runbooks — a plain `requestGraph` wrapper (R-A1).
 */
export function drillToArchDocs(projectKey = ''): void {
  requestGraph('AD', projectKey);
}

/**
 * Open the Human Guides (HG) dimension (R-A2 named action) — user/contributor guides
 * (`docs/guides/*.md`). No arg = the workspace HG index; a projectKey = that project's guides.
 * Same fetch lifecycle as drill/setpoints/runbooks — a plain `requestGraph` wrapper (R-A1).
 */
export function drillToGuides(projectKey = ''): void {
  requestGraph('HG', projectKey);
}

/** Breadcrumb jump (R-A2 named action). */
export function goToCrumb(crumb: ArchCrumb): void {
  requestGraph(crumb.level, crumb.id);
}

/** Open the node panel for a node (data-only self-loop in `rendered`). */
export function selectNode(nodeId: string): void {
  useArchStore.setState({ selectedNodeId: nodeId });
}

/** Close the node panel. */
export function closeNode(): void {
  useArchStore.setState({ selectedNodeId: null });
}

/**
 * Parse a `childTarget`/`seTarget`/`roadmapTarget`/`rbTarget`/`adTarget`/`hgTarget` → {level,id}.
 * Handles the C4 levels (`#arch/L1/<id>`, `#arch/L2/<key>/<container>`), the Setpoint Atlas
 * (`#arch/SE/<project>`, `#arch/SE/<project>/<suite>`), the Roadmap tier (`#arch/RM/<project>`), the
 * Runbooks tier (`#arch/RB/<project>`, `#arch/RB/workspace`), the Architecture Docs tier
 * (`#arch/AD/<project>`), AND the Human Guides tier (`#arch/HG/<project>`). The id segment may itself
 * contain slashes. This is the single drill chokepoint — a new token unparsed here silently no-ops
 * the drill.
 */
export function parseArchTarget(target: string): { level: string; id: string } | null {
  const m = target.match(/^#arch\/(L\d|SE|RM|RB|AD|HG)\/(.+)$/);
  return m ? { level: m[1], id: m[2] } : null;
}

/** Parse a select href (`#arch/n/<id>`) → node id. */
export function parseSelectHref(href: string): string | null {
  const m = href.match(/^#arch\/n\/(.+)$/);
  return m ? m[1] : null;
}

// The shareable `#arch/<level>/<id>` deep-link (hash reflect on navigation + cold-load restore) is
// implemented in ArchPage (the wire adapter) via `parseArchHash`/`buildArchHash` (utils/archUrl.ts) +
// `restoreFromHash` above. Coexistence with the OTHER two hash schemes on `/arch`:
//   - Session `#s/<id>` (useChatShell/useSocket) — that hook's mount effect + popstate handler are
//     now guarded to `pathname === '/'` (its ownership is the chat route only), so it never touches
//     `/arch`'s hash. See useChatShell.ts.
//   - CodeViewer `#file:` — CodeViewer owns any hash starting with `#file:`; `parseArchHash` returns
//     null for those, so ArchPage's popstate listener never calls `restoreFromHash` for a `#file:`
//     change — it's a no-op pass-through, left entirely to CodeViewer's own listener.
// See plans/arch-map-mobile-redesign.md § Navigation & state model + § "Dual-popstate contract".

// ─── Hook selectors ─────────────────────────────────────────────────────────────
export function useArchPhase(): ArchPhase {
  return useArchStore((s) => s.phase);
}
export function useArchGraph(): ArchGraphWire | null {
  return useArchStore(useShallow((s) => s.graph));
}
export function useArchCurrent(): { level: string; id: string } {
  return useArchStore(useShallow((s) => s.current));
}
export function useArchSelectedNodeId(): string | null {
  return useArchStore((s) => s.selectedNodeId);
}
export function useArchSearchQuery(): string {
  return useArchStore((s) => s.searchQuery);
}
export function useArchCrossQuery(): string | null {
  return useArchStore((s) => s.crossQuery);
}
export function useArchSeIndex(): SEIndexRow[] | null {
  return useArchStore(useShallow((s) => s.seIndex));
}
export function useArchFetchNonce(): number {
  return useArchStore((s) => s.fetchNonce);
}
export function useArchError(): string | null {
  return useArchStore((s) => s.error);
}
